// Panel Debug / Diagnostics — module isolé.
//
// Trois sections :
//   1. Qualité de prise en main — démarre/arrête l'enregistrement Helper
//      (diag_quality_*), affiche la timeline (debug_quality_tail) : fps cible
//      vs réel, drops, jitter, mode timer, cpu Helper, taille de conversion.
//   2. Snapshot / Watch — CPU/RAM des process RemoteWS (debug_snapshot, ou flux
//      live debug_watch_*).
//   3. Plugins — état logique (diag_plugins) joint à la RAM/CPU par worker.
//
// diag_* = outils agent-natifs (toujours dispo). debug_* = plugin isolé
// (installé à la demande). Le panel reste partiellement utile sans le plugin.

import { createSignal, createEffect, onCleanup, onMount, For, Show, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { isEnvelope } from "@/shared/ws-protocol";
import { formatBytes } from "@/shared/format-utils";
import { registerPanel } from "@/panel-registry";
import { PanelChrome } from "../console/ui";

interface ProcRow {
  pid: number;
  ppid: number | null;
  name: string;
  kind: string;
  pluginId: string | null;
  cpuPct: number;
  memBytes: number;
  handles: number;
  threads: number;
}

interface QualityRow {
  ts: string;
  fpsTarget: number;
  captureFps: number;
  encodeFps: number;
  drop: number;
  skip: number;
  scaleMs: number;
  convertMs: number;
  mftMs: number;
  paceJitterP50: number;
  paceJitterP95: number;
  timerMode: string;
  helperCpuPct: number;
  wsMb: number;
  privMb: number;
  codec: string;
  inW: number;
  inH: number;
  outW: number;
  outH: number;
  bytesPerFrame: number;
  bitrateTargetKbps: number;
  bitrateActualKbps: number;
  keyframes: number;
}

// Lecture tolérante au casing (les records C# sortent en PascalCase).
function pick<T = unknown>(o: Record<string, unknown> | undefined, ...keys: string[]): T | undefined {
  if (!o) return undefined;
  for (const k of keys) {
    if (o[k] !== undefined) return o[k] as T;
    const cap = k.charAt(0).toUpperCase() + k.slice(1);
    if (o[cap] !== undefined) return o[cap] as T;
  }
  return undefined;
}

const DebugPanel: Component = () => {
  const ws = useWs();
  const client = useToolClient();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;
  const pluginInstalled = () => Boolean(ws.state.pluginVersions["debug"]);

  const [status, setStatus] = createSignal<PanelStatus | undefined>();

  // ── Qualité ──────────────────────────────────────────────────────────────
  const [recording, setRecording] = createSignal(false);
  const [recFile, setRecFile] = createSignal<string | null>(null);
  const [qRows, setQRows] = createSignal<QualityRow[]>([]);
  const [label, setLabel] = createSignal("session");

  async function qualityStart() {
    setStatus({ text: "Démarrage de l'enregistrement qualité…", level: "info" });
    try {
      const r = (await client.call(
        "diag_quality_start",
        { label: label().trim() || "session" },
        { timeoutMs: 10_000 },
      )) as {
        active?: boolean;
        file?: string;
      };
      setRecording(r.active === true);
      setRecFile(r.file ?? null);
      setStatus({ text: "Enregistrement actif", level: "info" });
    } catch (e) {
      setStatus({ text: `Échec : ${errMsg(e)}`, level: "danger" });
    }
  }

  async function qualityStop() {
    try {
      await client.call("diag_quality_stop", {}, { timeoutMs: 10_000 });
      setRecording(false);
      setStatus({ text: "Enregistrement arrêté", level: "info" });
      void qualityTail();
    } catch (e) {
      setStatus({ text: `Échec : ${errMsg(e)}`, level: "danger" });
    }
  }

  async function qualityTail() {
    try {
      const r = (await client.call("debug_quality_tail", { limit: 120 }, { timeoutMs: 15_000 })) as {
        file?: string | null;
        rows?: QualityRow[];
      };
      setQRows(r.rows ?? []);
      if (r.file) setRecFile(r.file);
      if (!r.rows || r.rows.length === 0)
        setStatus({ text: "Aucune donnée qualité (plugin debug requis pour la lecture)", level: "warn" });
    } catch (e) {
      setStatus({ text: `Lecture qualité : ${errMsg(e)} — le plugin debug est-il installé ?`, level: "danger" });
    }
  }

  // ── Snapshot / Watch ──────────────────────────────────────────────────────
  const [procs, setProcs] = createSignal<ProcRow[]>([]);
  const [live, setLive] = createSignal(false);
  let jobId: string | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let offWs: (() => void) | null = null;

  async function snapshot() {
    setStatus({ text: "Snapshot en cours…", level: "info" });
    try {
      const r = (await client.call("debug_snapshot", {}, { timeoutMs: 15_000 })) as { procs?: ProcRow[] };
      setProcs(r.procs ?? []);
      setStatus({ text: `Snapshot : ${r.procs?.length ?? 0} process RemoteWS`, level: "info" });
    } catch (e) {
      setStatus({ text: `Snapshot : ${errMsg(e)} — le plugin debug est-il installé ?`, level: "danger" });
    }
  }

  async function startWatch() {
    if (live()) return;
    try {
      const r = (await client.call("debug_watch_start", { intervalMs: 3000 }, { timeoutMs: 15_000 })) as {
        jobId: string;
      };
      jobId = r.jobId;
      offWs?.();
      offWs = ws.on(
        (m) => isEnvelope(m) && m.family === "live" && m.type === "debug" && m.op.startsWith("watch."),
        (m) => {
          const msg = m as { op?: string; payload?: { jobId?: string; procs?: ProcRow[] } };
          if (msg.payload?.jobId && jobId && msg.payload.jobId !== jobId) return;
          if (msg.op === "watch.snapshot") setProcs(msg.payload?.procs ?? []);
          else if (msg.op === "watch.stopped") setLive(false);
        },
      );
      setLive(true);
      pingTimer = setInterval(() => {
        if (jobId) void client.call("debug_watch_ping", { jobId }, { timeoutMs: 5_000 }).catch(() => {});
      }, 8_000);
      setStatus({ text: "Suivi live actif (3s)", level: "info" });
    } catch (e) {
      setStatus({ text: `Watch : ${errMsg(e)}`, level: "danger" });
    }
  }

  async function stopWatch() {
    setLive(false);
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = null;
    offWs?.();
    offWs = null;
    const jid = jobId;
    jobId = null;
    try {
      await client.call("debug_watch_stop", jid ? { jobId: jid } : {}, { timeoutMs: 10_000 });
    } catch {
      /* le plugin s'arrête aussi de lui-même */
    }
  }

  // ── Plugins ───────────────────────────────────────────────────────────────
  const [pluginRows, setPluginRows] = createSignal<Array<Record<string, unknown>>>([]);
  const [hostMem, setHostMem] = createSignal<Record<string, unknown> | null>(null);
  const [sticky, setSticky] = createSignal<string[]>([]);

  async function loadPlugins() {
    try {
      const r = (await client.call("diag_plugins", {}, { timeoutMs: 15_000 })) as Record<string, unknown>;
      const plugins = (pick<Array<Record<string, unknown>>>(r, "plugins") ?? []).slice();
      setPluginRows(plugins);
      setSticky((pick<string[]>(r, "sticky") ?? []) as string[]);
      setHostMem((pick<Record<string, unknown>>(r, "host") ?? null) as Record<string, unknown> | null);
      setStatus({ text: `${plugins.length} plugin(s) chargé(s)`, level: "info" });
    } catch (e) {
      setStatus({ text: `Plugins : ${errMsg(e)}`, level: "danger" });
    }
  }

  // RAM par plugin issue du dernier snapshot (workers isolation: process).
  const pluginMem = (id: string): ProcRow | undefined => procs().find((p) => p.pluginId === id);

  createEffect(() => {
    if (enabled()) {
      void loadPlugins();
    }
  });

  onMount(() => {
    if (enabled()) {
      void snapshot();
      void loadPlugins();
      void qualityTail();
    }
  });
  onCleanup(() => {
    if (live()) void stopWatch();
    if (pingTimer) clearInterval(pingTimer);
    offWs?.();
  });

  const latest = () => qRows()[qRows().length - 1];

  const actions = (
    <>
      <button class="btn btn--xs" onClick={() => void snapshot()} disabled={!enabled()}>
        Snapshot
      </button>
      <button
        class={`btn btn--xs ${live() ? "btn--ok" : "btn--ghost"}`}
        onClick={() => (live() ? void stopWatch() : void startWatch())}
        disabled={!enabled()}
      >
        {live() ? "● Live" : "○ Live"}
      </button>
      <button class="btn btn--xs" onClick={() => void loadPlugins()} disabled={!enabled()}>
        Plugins
      </button>
    </>
  );

  return (
    <PanelChrome icon="📊" title="Debug / Diagnostics" tagType="core" actions={actions}>
      <PanelLayout title="Debug / Diagnostics" status={status()}>
        <div style={{ display: "flex", "flex-direction": "column", gap: "16px", padding: "8px" }}>
          {/* ── Section Qualité ── */}
          <section>
            <h3 style={{ margin: "0 0 6px" }}>Qualité de prise en main</h3>
            <div
              style={{
                display: "flex",
                gap: "8px",
                "align-items": "center",
                "flex-wrap": "wrap",
                "margin-bottom": "8px",
              }}
            >
              <input
                class="panel-filter-input"
                placeholder="libellé"
                value={label()}
                onInput={(e) => setLabel(e.currentTarget.value)}
                style={{ width: "140px" }}
              />
              <Show
                when={recording()}
                fallback={
                  <button class="btn btn--xs btn--primary" onClick={() => void qualityStart()} disabled={!enabled()}>
                    ● Démarrer l'enregistrement
                  </button>
                }
              >
                <button class="btn btn--xs btn--danger" onClick={() => void qualityStop()}>
                  ■ Arrêter
                </button>
              </Show>
              <button class="btn btn--xs" onClick={() => void qualityTail()} disabled={!enabled()}>
                Rafraîchir
              </button>
              <Show when={recFile()}>
                <code style={{ "font-size": "11px", opacity: 0.7 }}>{recFile()}</code>
              </Show>
            </div>

            <Show
              when={latest()}
              fallback={<div style={{ opacity: 0.6 }}>Pas de données — démarrer l'enregistrement puis Rafraîchir.</div>}
            >
              {(l) => (
                <div
                  style={{
                    display: "flex",
                    gap: "16px",
                    "flex-wrap": "wrap",
                    "font-size": "13px",
                    "margin-bottom": "8px",
                  }}
                >
                  <Metric
                    label="FPS"
                    value={`${l().captureFps} / ${l().encodeFps}`}
                    sub={`cible ${l().fpsTarget}`}
                    danger={Math.abs(l().captureFps - l().fpsTarget) > 2 || Math.abs(l().encodeFps - l().fpsTarget) > 2}
                  />
                  <Metric label="Drops" value={String(l().drop)} danger={l().drop > 0} />
                  <Metric
                    label="Jitter p95"
                    value={`${l().paceJitterP95} ms`}
                    danger={l().paceJitterP95 > l().fpsTarget}
                  />
                  <Metric label="Timer" value={l().timerMode} danger={l().timerMode !== "highres"} />
                  <Metric label="CPU Helper" value={`${l().helperCpuPct}%`} danger={l().helperCpuPct > 25} />
                  <Metric
                    label="Encode ms"
                    value={`${l().scaleMs}/${l().convertMs}/${l().mftMs}`}
                    sub="scale/conv/mft"
                  />
                  <Metric
                    label="Conversion"
                    value={`${l().inW}×${l().inH} → ${l().outW}×${l().outH}`}
                    sub={`${formatBytes(l().bytesPerFrame)}/frame`}
                  />
                  <Metric
                    label="Bitrate"
                    value={`${l().bitrateActualKbps} kbps`}
                    sub={`cible ${l().bitrateTargetKbps}`}
                  />
                  <Metric label="RAM Helper" value={`${l().wsMb} Mo`} />
                </div>
              )}
            </Show>

            <Show when={qRows().length > 0}>
              <details>
                <summary style={{ cursor: "pointer", "font-size": "12px" }}>Timeline ({qRows().length} pts)</summary>
                <div style={{ "max-height": "180px", overflow: "auto" }}>
                  <table class="proc-tree" style={{ "font-size": "11px", width: "100%" }}>
                    <thead>
                      <tr>
                        <th>ts</th>
                        <th>capFps</th>
                        <th>encFps</th>
                        <th>drop</th>
                        <th>jitterP95</th>
                        <th>timer</th>
                        <th>cpu%</th>
                        <th>kbps</th>
                      </tr>
                    </thead>
                    <tbody>
                      <For each={qRows().slice().reverse()}>
                        {(r) => (
                          <tr>
                            <td>{(r.ts ?? "").slice(11, 19)}</td>
                            <td
                              style={{
                                color: Math.abs(r.captureFps - r.fpsTarget) > 2 ? "var(--danger,#dc2626)" : undefined,
                              }}
                            >
                              {r.captureFps}
                            </td>
                            <td>{r.encodeFps}</td>
                            <td>{r.drop}</td>
                            <td>{r.paceJitterP95}</td>
                            <td>{r.timerMode}</td>
                            <td>{r.helperCpuPct}</td>
                            <td>{r.bitrateActualKbps}</td>
                          </tr>
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </details>
            </Show>
          </section>

          {/* ── Section Snapshot / Watch ── */}
          <section>
            <h3 style={{ margin: "0 0 6px" }}>Process RemoteWS ({procs().length})</h3>
            <table class="proc-tree" style={{ "font-size": "12px", width: "100%" }}>
              <thead>
                <tr>
                  <th>Process</th>
                  <th>Type</th>
                  <th>Plugin</th>
                  <th>CPU</th>
                  <th>RAM</th>
                  <th>Handles</th>
                  <th>Threads</th>
                </tr>
              </thead>
              <tbody>
                <For
                  each={procs()}
                  fallback={
                    <tr>
                      <td colspan="7" style={{ opacity: 0.6 }}>
                        Cliquer « Snapshot ».
                      </td>
                    </tr>
                  }
                >
                  {(p) => (
                    <tr>
                      <td>
                        {p.name} <span style={{ opacity: 0.5 }}>#{p.pid}</span>
                      </td>
                      <td>{p.kind}</td>
                      <td>{p.pluginId ?? "—"}</td>
                      <td style={{ color: p.cpuPct > 25 ? "var(--danger,#dc2626)" : undefined }}>{p.cpuPct}%</td>
                      <td>{formatBytes(p.memBytes)}</td>
                      <td>{p.handles}</td>
                      <td>{p.threads}</td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </section>

          {/* ── Section Plugins ── */}
          <section>
            <h3 style={{ margin: "0 0 6px" }}>Plugins chargés</h3>
            <Show
              when={pluginInstalled()}
              fallback={
                <div style={{ "font-size": "12px", opacity: 0.7, "margin-bottom": "6px" }}>
                  Plugin Debug manquant : la section « Plugins » et les métriques Snapshot/Live sont désactivées.
                </div>
              }
            >
              <Show when={hostMem()}>
                {(h) => (
                  <div style={{ "font-size": "12px", opacity: 0.8, "margin-bottom": "6px" }}>
                    PluginHost (shared) : WS {String(pick(h(), "wsMb"))} Mo · priv {String(pick(h(), "privMb"))} Mo ·
                    heap {String(pick(h(), "heapMb"))} Mo · GC commit {String(pick(h(), "gcCommitMb"))} Mo · frag{" "}
                    {String(pick(h(), "fragMb"))} Mo
                  </div>
                )}
              </Show>
            </Show>
            <table class="proc-tree" style={{ "font-size": "12px", width: "100%" }}>
              <thead>
                <tr>
                  <th>Plugin</th>
                  <th>Jobs</th>
                  <th>PS sess.</th>
                  <th>Idle (s)</th>
                  <th>Persistant</th>
                  <th>RAM (worker)</th>
                </tr>
              </thead>
              <tbody>
                <For
                  each={pluginRows()}
                  fallback={
                    <tr>
                      <td colspan="6" style={{ opacity: 0.6 }}>
                        Cliquer « Plugins ».
                      </td>
                    </tr>
                  }
                >
                  {(pl) => {
                    const id = String(pick(pl, "pluginId") ?? "?");
                    const mem = pluginMem(id);
                    const isSticky = () => sticky().includes(id);
                    return (
                      <tr>
                        <td>
                          {id}
                          <Show when={isSticky()}>
                            <span
                              style={{ color: "var(--danger,#dc2626)", "margin-left": "4px" }}
                              title="sticky (ALC non déchargeable)"
                            >
                              ⚠
                            </span>
                          </Show>
                        </td>
                        <td>{String(pick(pl, "activeJobs") ?? 0)}</td>
                        <td>{String(pick(pl, "openPsSessions") ?? 0)}</td>
                        <td>{String(pick(pl, "idleSecs") ?? 0)}</td>
                        <td>{pick<boolean>(pl, "hasPersistentState") ? "oui" : "non"}</td>
                        <td>{mem ? formatBytes(mem.memBytes) : "shared"}</td>
                      </tr>
                    );
                  }}
                </For>
              </tbody>
            </table>
          </section>
        </div>
      </PanelLayout>
    </PanelChrome>
  );
};

const Metric: Component<{ label: string; value: string; sub?: string; danger?: boolean }> = (props) => (
  <div style={{ "min-width": "90px" }}>
    <div style={{ "font-size": "11px", opacity: 0.6 }}>{props.label}</div>
    <div style={{ "font-weight": "600", color: props.danger ? "var(--danger,#dc2626)" : undefined }}>{props.value}</div>
    <Show when={props.sub}>
      <div style={{ "font-size": "10px", opacity: 0.5 }}>{props.sub}</div>
    </Show>
  </div>
);

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

registerPanel("debug", DebugPanel);
export default DebugPanel;
